import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { MuscleGroup, Routine as HevyRoutine } from '../types'
import { exerciseProcessor } from '../services/exerciseProcessor'
import { DAYS_OF_WEEK, ID_LENGTH, MIN_SET_COUNT, DEFAULT_SET_COUNT, type DayOfWeek } from '../lib/constants'

export type SetType = 'normal' | 'warmup' | 'failure' | 'dropset'

export interface ExerciseSet {
  id: string
  reps: number
  weight: number | null // in kg
  type: SetType
}

export interface SplitExercise {
  id: string // exercise template ID
  name: string
  sets: ExerciseSet[]
  primaryMover: MuscleGroup
  secondaryMovers: MuscleGroup[]
  isCustom: boolean
  order: number // for sorting within the day
}

export interface Routine {
  id: string
  name: string
  exercises: SplitExercise[]
}

export type MuscleGroupVolume = {
  [K in MuscleGroup]: number
}

interface SplitConfigState {
  // Split configuration
  splitName: string
  routines: Record<string, Routine> // routines by ID
  dayAssignments: Record<DayOfWeek, string | null> // day -> routine ID or null (normal mode)
  cycleAssignments: (string | null)[] // routine IDs for cycle days (cycle mode)
  cycleMode: boolean
  
  // Real-time calculations
  weeklyVolume: MuscleGroupVolume
  weeklyVolumePrimaryOnly: MuscleGroupVolume
  totalSets: number
  
  // Actions
  setSplitName: (name: string) => void
  toggleCycleMode: () => void
  addCycleDay: () => void
  removeCycleDay: (dayIndex: number) => void
  
  // Routine management
  createRoutine: (name: string) => string // returns routine ID
  createRoutineFromHevy: (hevyRoutine: HevyRoutine) => string // returns routine ID
  updateRoutineName: (routineId: string, name: string) => void
  deleteRoutine: (routineId: string) => void
  
  // Exercise management within routines
  addExerciseToRoutine: (routineId: string, exercise: Omit<SplitExercise, 'order'>) => void
  addExerciseWithLastWorkoutData: (routineId: string, exercise: Omit<SplitExercise, 'order' | 'sets'>, lastWorkoutSets: ExerciseSet[]) => void
  removeExerciseFromRoutine: (routineId: string, exerciseId: string) => void
  updateExerciseInRoutine: (routineId: string, exerciseId: string, updates: Partial<SplitExercise>) => void
  reorderExercisesInRoutine: (routineId: string, fromIndex: number, toIndex: number) => void
  
  // Set management
  addSetToExercise: (routineId: string, exerciseId: string) => void
  removeSetFromExercise: (routineId: string, exerciseId: string, setId: string) => void
  updateSetInExercise: (routineId: string, exerciseId: string, setId: string, updates: Partial<ExerciseSet>) => void
  
  // Day assignment
  assignRoutineToDay: (day: DayOfWeek, routineId: string) => void
  unassignRoutineFromDay: (day: DayOfWeek) => void
  assignRoutineToCycleDay: (dayIndex: number, routineId: string) => void
  unassignRoutineFromCycleDay: (dayIndex: number) => void
  
  // Volume calculation
  calculateVolume: () => void
  
  // Clear all data
  clearAllData: () => void
  
  // Computed getters
  getRoutineForDay: (day: DayOfWeek) => Routine | null
  getDaysForRoutine: (routineId: string) => DayOfWeek[]
}

const createEmptyDayAssignments = (): Record<DayOfWeek, string | null> => {
  return DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day] = null
    return acc
  }, {} as Record<DayOfWeek, string | null>)
}

const generateId = () => Math.random().toString(36).substring(2, 2 + ID_LENGTH)

const createEmptyVolume = (): MuscleGroupVolume => ({
  chest: 0,
  lats: 0,
  upperBack: 0,
  rearDelts: 0,
  lateralDelts: 0,
  frontDelts: 0,
  biceps: 0,
  triceps: 0,
  quads: 0,
  hamstrings: 0,
  glutes: 0,
  adductors: 0,
  calves: 0,
  abs: 0,
  forearms: 0,
  neck: 0
})

export const useSplitConfigStore = create<SplitConfigState>()(
  persist(
    (set, get) => ({
      // Split configuration (will be persisted)
      splitName: '',
      routines: {},
      dayAssignments: createEmptyDayAssignments(),
      cycleAssignments: [],
      cycleMode: false,
      
      // Calculated values (will be persisted for performance)
      weeklyVolume: createEmptyVolume(),
      weeklyVolumePrimaryOnly: createEmptyVolume(),
      totalSets: 0,
      
      // Actions
      setSplitName: (name) => {
        set({ splitName: name })
      },
      
      toggleCycleMode: () => {
        set((state) => {
          const newCycleMode = !state.cycleMode
          
          if (newCycleMode) {
            // Switching to cycle mode - copy weekly assignments to cycle assignments
            const cycleAssignments: (string | null)[] = []
            DAYS_OF_WEEK.forEach((day) => {
              const routineId = state.dayAssignments[day]
              if (routineId) {
                cycleAssignments.push(routineId)
              }
            })
            
            return { cycleMode: true, cycleAssignments }
          } else {
            // Switching to normal mode - copy first 7 cycle assignments back to weekly
            const newDayAssignments = createEmptyDayAssignments()
            state.cycleAssignments.slice(0, 7).forEach((routineId, index) => {
              if (routineId) {
                newDayAssignments[DAYS_OF_WEEK[index]] = routineId
              }
            })
            
            return { cycleMode: false, dayAssignments: newDayAssignments }
          }
        })
        get().calculateVolume()
      },
      
      addCycleDay: () => {
        set((state) => {
          if (state.cycleAssignments.length >= 8) return state
          return { cycleAssignments: [...state.cycleAssignments, null] }
        })
      },
      
      removeCycleDay: (dayIndex) => {
        set((state) => {
          const newAssignments = [...state.cycleAssignments]
          newAssignments.splice(dayIndex, 1)
          return { cycleAssignments: newAssignments }
        })
        get().calculateVolume()
      },
      
      // Routine management
      createRoutine: (_name) => {
        const routineId = generateId()
        set((state) => ({
          routines: {
            ...state.routines,
            [routineId]: {
              id: routineId,
              name: '', // Start with empty name to show placeholder
              exercises: []
            }
          }
        }))
        return routineId
      },
      
      createRoutineFromHevy: (hevyRoutine) => {
        const routineId = generateId()
        
        // Convert Hevy routine exercises to our format
        const exercises: SplitExercise[] = hevyRoutine.exercises.map((ex, index: number) => {
          // Convert Hevy sets to our format
          const sets: ExerciseSet[] = ex.sets.map((set, setIndex: number) => ({
            id: `${ex.exercise_template_id}-set-${setIndex}-${Date.now()}`,
            reps: set.reps || 0,
            weight: set.weight_kg || null,
            type: 'normal' as SetType
          }))
          
          // Use centralized exercise processor for muscle group categorization
          const processedExercise = exerciseProcessor.processExercise({
            templateId: ex.exercise_template_id,
            name: ex.title
          })
          
          return {
            id: ex.exercise_template_id,
            name: ex.title,
            sets,
            primaryMover: processedExercise.primaryMover,
            secondaryMovers: processedExercise.secondaryMovers,
            isCustom: false,
            order: index
          }
        })
        
        set((state) => ({
          routines: {
            ...state.routines,
            [routineId]: {
              id: routineId,
              name: hevyRoutine.title || '',
              exercises
            }
          }
        }))
        
        return routineId
      },
      
      updateRoutineName: (routineId, name) => {
        set((state) => ({
          routines: {
            ...state.routines,
            [routineId]: {
              ...state.routines[routineId],
              name
            }
          }
        }))
      },
      
      deleteRoutine: (routineId) => {
        set((state) => {
          const newRoutines = { ...state.routines }
          delete newRoutines[routineId]
          
          // Unassign routine from all days
          const newDayAssignments = { ...state.dayAssignments }
          DAYS_OF_WEEK.forEach(day => {
            if (newDayAssignments[day] === routineId) {
              newDayAssignments[day] = null
            }
          })
          
          return {
            routines: newRoutines,
            dayAssignments: newDayAssignments
          }
        })
        get().calculateVolume()
      },
      
      // Exercise management within routines
      addExerciseToRoutine: (routineId, exerciseData) => {
        set((state) => {
          const routine = state.routines[routineId]
          if (!routine) return state
          
          const newOrder = routine.exercises.length > 0 
            ? Math.max(...routine.exercises.map(e => e.order)) + 1 
            : 0
          
          // Ensure muscle groups are properly processed using the centralized service
          const processedExercise = exerciseProcessor.processExercise({
            templateId: exerciseData.id,
            name: exerciseData.name
          })
          
          const newExercise: SplitExercise = {
            ...exerciseData,
            primaryMover: processedExercise.primaryMover,
            secondaryMovers: processedExercise.secondaryMovers,
            order: newOrder
          }
          
          return {
            routines: {
              ...state.routines,
              [routineId]: {
                ...routine,
                exercises: [...routine.exercises, newExercise]
              }
            }
          }
        })
        get().calculateVolume()
      },
      
      addExerciseWithLastWorkoutData: (routineId, exerciseData, lastWorkoutSets) => {
        set((state) => {
          const routine = state.routines[routineId]
          if (!routine) return state
          
          const newOrder = routine.exercises.length > 0 
            ? Math.max(...routine.exercises.map(e => e.order)) + 1 
            : 0
          
          // Ensure muscle groups are properly processed using the centralized service
          const processedExercise = exerciseProcessor.processExercise({
            templateId: exerciseData.id,
            name: exerciseData.name
          })
          
          // Create sets based on last workout data
          let sets: ExerciseSet[] = []
          
          if (lastWorkoutSets.length > 0) {
            // Pre-fill with the same number of sets from last workout
            sets = lastWorkoutSets.map((lastSet, i) => ({
              id: `${exerciseData.id}-set-${i}-${Date.now()}`,
              reps: lastSet.reps || 0,
              weight: lastSet.weight || null,
              type: lastSet.type || 'normal'
            }))
          } else {
            // No previous data, create default empty sets
            sets = Array.from({ length: DEFAULT_SET_COUNT }, (_, i) => ({
              id: `${exerciseData.id}-set-${i}-${Date.now()}`,
              reps: 0,
              weight: null,
              type: 'normal' as SetType
            }))
          }
          
          const newExercise: SplitExercise = {
            ...exerciseData,
            sets,
            primaryMover: processedExercise.primaryMover,
            secondaryMovers: processedExercise.secondaryMovers,
            order: newOrder
          }
          
          return {
            routines: {
              ...state.routines,
              [routineId]: {
                ...routine,
                exercises: [...routine.exercises, newExercise]
              }
            }
          }
        })
        get().calculateVolume()
      },
      
      removeExerciseFromRoutine: (routineId, exerciseId) => {
        set((state) => {
          const routine = state.routines[routineId]
          if (!routine) return state
          
          return {
            routines: {
              ...state.routines,
              [routineId]: {
                ...routine,
                exercises: routine.exercises.filter(exercise => exercise.id !== exerciseId)
              }
            }
          }
        })
        get().calculateVolume()
      },
      
      updateExerciseInRoutine: (routineId, exerciseId, updates) => {
        set((state) => {
          const routine = state.routines[routineId]
          if (!routine) return state
          
          return {
            routines: {
              ...state.routines,
              [routineId]: {
                ...routine,
                exercises: routine.exercises.map(exercise =>
                  exercise.id === exerciseId ? { ...exercise, ...updates } : exercise
                )
              }
            }
          }
        })
        get().calculateVolume()
      },
      
      reorderExercisesInRoutine: (routineId, fromIndex, toIndex) => {
        set((state) => {
          const routine = state.routines[routineId]
          if (!routine) return state
          
          const exercises = [...routine.exercises]
          const [movedExercise] = exercises.splice(fromIndex, 1)
          exercises.splice(toIndex, 0, movedExercise)
          
          // Update order values to match new positions
          const reorderedExercises = exercises.map((exercise, index) => ({
            ...exercise,
            order: index
          }))
          
          return {
            routines: {
              ...state.routines,
              [routineId]: {
                ...routine,
                exercises: reorderedExercises
              }
            }
          }
        })
      },
      
      // Set management
      addSetToExercise: (routineId, exerciseId) => {
        set((state) => {
          const routine = state.routines[routineId]
          if (!routine) return state
          
          const exerciseIndex = routine.exercises.findIndex(e => e.id === exerciseId)
          if (exerciseIndex === -1) return state
          
          const exercise = routine.exercises[exerciseIndex]
          
          // If there are existing sets, copy the last set's values
          const lastSet = exercise.sets.length > 0 ? exercise.sets[exercise.sets.length - 1] : null
          
          const newSet: ExerciseSet = {
            id: generateId(),
            reps: lastSet ? lastSet.reps : 0,
            weight: lastSet ? lastSet.weight : null,
            type: lastSet ? lastSet.type : 'normal'
          }
          
          const updatedExercise = {
            ...exercise,
            sets: [...exercise.sets, newSet]
          }
          
          const updatedExercises = [...routine.exercises]
          updatedExercises[exerciseIndex] = updatedExercise
          
          return {
            routines: {
              ...state.routines,
              [routineId]: {
                ...routine,
                exercises: updatedExercises
              }
            }
          }
        })
        get().calculateVolume()
      },
      
      removeSetFromExercise: (routineId, exerciseId, setId) => {
        set((state) => {
          const routine = state.routines[routineId]
          if (!routine) return state
          
          const exerciseIndex = routine.exercises.findIndex(e => e.id === exerciseId)
          if (exerciseIndex === -1) return state
          
          const exercise = routine.exercises[exerciseIndex]
          // Don't allow removing the last set
          if (exercise.sets.length <= MIN_SET_COUNT) return state
          
          const updatedExercise = {
            ...exercise,
            sets: exercise.sets.filter(set => set.id !== setId)
          }
          
          const updatedExercises = [...routine.exercises]
          updatedExercises[exerciseIndex] = updatedExercise
          
          return {
            routines: {
              ...state.routines,
              [routineId]: {
                ...routine,
                exercises: updatedExercises
              }
            }
          }
        })
        get().calculateVolume()
      },
      
      updateSetInExercise: (routineId, exerciseId, setId, updates) => {
        set((state) => {
          const routine = state.routines[routineId]
          if (!routine) return state
          
          const exerciseIndex = routine.exercises.findIndex(e => e.id === exerciseId)
          if (exerciseIndex === -1) return state
          
          const exercise = routine.exercises[exerciseIndex]
          const setIndex = exercise.sets.findIndex(s => s.id === setId)
          if (setIndex === -1) return state
          
          const updatedSets = [...exercise.sets]
          updatedSets[setIndex] = { ...updatedSets[setIndex], ...updates }
          
          const updatedExercise = {
            ...exercise,
            sets: updatedSets
          }
          
          const updatedExercises = [...routine.exercises]
          updatedExercises[exerciseIndex] = updatedExercise
          
          return {
            routines: {
              ...state.routines,
              [routineId]: {
                ...routine,
                exercises: updatedExercises
              }
            }
          }
        })
        // Note: We don't recalculate volume on weight changes, only on set count changes
      },
      
      // Day assignment
      assignRoutineToDay: (day, routineId) => {
        set((state) => ({
          dayAssignments: {
            ...state.dayAssignments,
            [day]: routineId
          }
        }))
        get().calculateVolume()
      },
      
      unassignRoutineFromDay: (day) => {
        set((state) => ({
          dayAssignments: {
            ...state.dayAssignments,
            [day]: null
          }
        }))
        get().calculateVolume()
      },
      
      assignRoutineToCycleDay: (dayIndex, routineId) => {
        set((state) => {
          const newAssignments = [...state.cycleAssignments]
          newAssignments[dayIndex] = routineId
          return { cycleAssignments: newAssignments }
        })
        get().calculateVolume()
      },
      
      unassignRoutineFromCycleDay: (dayIndex) => {
        set((state) => {
          const newAssignments = [...state.cycleAssignments]
          newAssignments[dayIndex] = null
          return { cycleAssignments: newAssignments }
        })
        get().calculateVolume()
      },
      
      calculateVolume: () => {
        const state = get()
        const volume = createEmptyVolume()
        const volumePrimaryOnly = createEmptyVolume()
        let totalSets = 0
        
        if (state.cycleMode) {
          // Calculate volume from cycle assignments
          state.cycleAssignments.forEach((routineId) => {
            if (routineId && state.routines[routineId]) {
              state.routines[routineId].exercises.forEach(exercise => {
                const setCount = exercise.sets.length
                
                // Add sets to primary muscle (full contribution)
                volume[exercise.primaryMover as keyof MuscleGroupVolume] += setCount
                volumePrimaryOnly[exercise.primaryMover as keyof MuscleGroupVolume] += setCount
                
                // Add sets to secondary muscles (half contribution) - only to full volume
                exercise.secondaryMovers.forEach(muscle => {
                  volume[muscle as keyof MuscleGroupVolume] += setCount * 0.5
                })
                
                totalSets += setCount
              })
            }
          })
          
          // Normalize to weekly volume if we have cycle days
          const cycleDays = state.cycleAssignments.length
          if (cycleDays > 0) {
            const normalizationFactor = 7 / cycleDays
            Object.keys(volume).forEach((muscle) => {
              volume[muscle as keyof MuscleGroupVolume] *= normalizationFactor
              volumePrimaryOnly[muscle as keyof MuscleGroupVolume] *= normalizationFactor
            })
            totalSets = Math.round(totalSets * normalizationFactor)
          }
        } else {
          // Calculate volume from weekly assignments (original logic)
          DAYS_OF_WEEK.forEach(day => {
            const routineId = state.dayAssignments[day]
            if (routineId && state.routines[routineId]) {
              state.routines[routineId].exercises.forEach(exercise => {
                const setCount = exercise.sets.length
                
                // Add sets to primary muscle (full contribution)
                volume[exercise.primaryMover as keyof MuscleGroupVolume] += setCount
                volumePrimaryOnly[exercise.primaryMover as keyof MuscleGroupVolume] += setCount
                
                // Add sets to secondary muscles (half contribution) - only to full volume
                exercise.secondaryMovers.forEach(muscle => {
                  volume[muscle as keyof MuscleGroupVolume] += setCount * 0.5
                })
                
                totalSets += setCount
              })
            }
          })
        }
        
        set({ weeklyVolume: volume, weeklyVolumePrimaryOnly: volumePrimaryOnly, totalSets })
      },
      
      // Clear all data
      clearAllData: () => {
        set({
          splitName: '',
          routines: {},
          dayAssignments: createEmptyDayAssignments(),
          cycleAssignments: [],
          cycleMode: false,
          weeklyVolume: createEmptyVolume(),
          weeklyVolumePrimaryOnly: createEmptyVolume(),
          totalSets: 0
        })
      },
      
      // Computed getters
      getRoutineForDay: (day) => {
        const state = get()
        const routineId = state.dayAssignments[day]
        return routineId ? state.routines[routineId] || null : null
      },
      
      getDaysForRoutine: (routineId) => {
        const state = get()
        return DAYS_OF_WEEK.filter(day => state.dayAssignments[day] === routineId)
      }
    }),
    {
      name: 'loadline-split-config',
      version: 1,
      // Persist all split configuration data
      partialize: (state) => ({
        splitName: state.splitName,
        routines: state.routines,
        dayAssignments: state.dayAssignments,
        cycleAssignments: state.cycleAssignments,
        cycleMode: state.cycleMode,
        weeklyVolume: state.weeklyVolume,
        weeklyVolumePrimaryOnly: state.weeklyVolumePrimaryOnly,
        totalSets: state.totalSets
      })
    }
  )
)